<template>
  <div class="driveVue">
    <div class="o-main-content-area c-responsive-fixwidth">
      <!-- Header -->
      <div class="row">
        <div class="col-md-12">
          <h1 class="h1">Drive</h1>
          <div class="o-subtitleHeading">
            <p class="h3">
              <i class="fa fa-cloud" aria-hidden="true"></i>
              Google Drive file portal — browse, preview, and download shared files.
            </p>
          </div>
        </div>
      </div>

      <!-- Breadcrumbs -->
      <DriveBreadcrumbs
        :breadcrumbs="breadcrumbs"
        @navigate="navigateToFolder"
      />

      <!-- Action Bar -->
      <DriveActionBar
        :search-query="searchQuery"
        :view-mode="viewMode"
        :is-admin="isAdmin"
        :can-upload="canUpload"
        @search="onSearch"
        @toggle-view="toggleViewMode"
        @upload="showUploadDialog = true"
        @new-folder="promptNewFolder"
        @manage-groups="showGroupManager = true"
      />

      <!-- File List -->
      <div v-loading="loading" class="o-filesList">
        <DriveFileList
          v-if="files.length > 0 || loading"
          :files="files"
          :view-mode="viewMode"
          :is-admin="isAdmin"
          @open-folder="navigateToFolder"
          @preview-file="openPreview"
          @open-payroll-workbook="openPayrollWorkbook"
          @download-file="downloadFile"
          @share-file="openShareDialog"
          @rename-file="promptRename"
          @move-file="promptMove"
          @delete-file="promptDelete"
          @toggle-restriction="toggleRestriction"
          @toggle-visibility="toggleVisibility"
          @set-deadline="openDeadlineDialog"
          @edit-text-file="openTextEditor"
        />

        <!-- Empty state -->
        <div
          v-if="files.length === 0 && afterApi && !loading && !noDriveConfigured"
          class="o-filesListBox"
        >
          <div class="c-common-box-shadow c-bg-white" style="padding: 40px; text-align: center;">
            <i class="fa fa-folder-open-o" aria-hidden="true" style="font-size: 48px; color: #ccc; display: block; margin-bottom: 16px;"></i>
            <template v-if="searchQuery">
              <p style="color: #999;">No files matching "{{ searchQuery }}"</p>
            </template>
            <template v-else>
              <p style="color: #999;">This folder is empty</p>
              <p v-if="isAdmin" style="color: #bbb; font-size: 13px;">
                Use the Upload or New Folder buttons above to get started.
              </p>
            </template>
          </div>
        </div>

        <!-- Error state -->
        <div v-if="error" class="o-filesListBox">
          <div class="c-common-box-shadow c-bg-white" style="padding: 40px; text-align: center;">
            <i class="fa fa-exclamation-triangle" aria-hidden="true" style="font-size: 48px; color: #c0392b; display: block; margin-bottom: 16px;"></i>
            <p style="color: #c0392b;">{{ error }}</p>
          </div>
        </div>

        <!-- No Drive configured -->
        <div v-if="noDriveConfigured && afterApi && !loading" class="o-filesListBox">
          <div class="c-common-box-shadow c-bg-white" style="padding: 40px; text-align: center;">
            <i class="fa fa-hdd-o" aria-hidden="true" style="font-size: 48px; color: #ccc; display: block; margin-bottom: 16px;"></i>
            <h3 style="color: #666;">CS Drive Portal</h3>
            <p style="color: #999; margin-top: 8px;">
              No Drive has been configured for this client yet.
            </p>
          </div>
        </div>
      </div>

      <!-- Preview Panel -->
      <DrivePreviewPanel
        v-if="previewFile"
        :file="previewFile"
        :is-admin="isAdmin"
        @close="closePreview"
        @download="downloadFile"
        @set-deadline="openDeadlineDialog"
      />

      <!-- Upload Dialog -->
      <DriveUploadDialog
        v-if="showUploadDialog"
        :folder-id="currentFolderId"
        @upload-file="handleUploadFile"
        @uploaded="onUploadComplete"
        @close="showUploadDialog = false"
      />

      <!-- Move Dialog -->
      <DriveMoveDialog
        v-if="moveFile"
        :file="moveFile"
        :root-drive-id="rootDriveId"
        @load-folders="handleLoadFolders"
        @confirm-move="handleMove"
        @close="moveFile = null"
      />

      <!-- Share Dialog -->
      <DriveShareDialog
        v-if="shareFile"
        :file="shareFile"
        :api-headers="apiHeaders"
        @permissions-changed="fetchFiles"
        @close="shareFile = null"
      />

      <!-- Group Manager -->
      <DriveGroupManager
        v-if="showGroupManager"
        :api-headers="apiHeaders"
        @close="showGroupManager = false"
      />

      <!-- Deadline Dialog -->
      <DriveDeadlineDialog
        v-if="deadlineFile"
        :file="deadlineFile"
        :api-headers="apiHeaders"
        @deadline-set="onDeadlineChanged"
        @deadline-removed="onDeadlineChanged"
        @close="deadlineFile = null"
      />

      <!-- Text Edit Dialog -->
      <DriveTextEditDialog
        v-if="editTextFile"
        :file="editTextFile"
        :api-headers="apiHeaders"
        @save="handleSaveTextFile"
        @close="editTextFile = null"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getApiBase, safeSetXhrHeaders } from "../../global.js";
import service from "../../services.js";
import DriveBreadcrumbs from "./DriveBreadcrumbs.vue";
import DriveActionBar from "./DriveActionBar.vue";
import DriveFileList from "./DriveFileList.vue";
import DrivePreviewPanel from "./DrivePreviewPanel.vue";
import DriveUploadDialog from "./DriveUploadDialog.vue";
import DriveMoveDialog from "./DriveMoveDialog.vue";
import DriveShareDialog from "./DriveShareDialog.vue";
import DriveGroupManager from "./DriveGroupManager.vue";
import DriveDeadlineDialog from "./DriveDeadlineDialog.vue";
import DriveTextEditDialog from "./DriveTextEditDialog.vue";

export default {
  name: "DriveIndex",
  components: {
    DriveBreadcrumbs,
    DriveActionBar,
    DriveFileList,
    DrivePreviewPanel,
    DriveUploadDialog,
    DriveMoveDialog,
    DriveShareDialog,
    DriveGroupManager,
    DriveDeadlineDialog,
    DriveTextEditDialog,
  },
  data() {
    return {
      loading: false,
      afterApi: false,
      files: [],
      breadcrumbs: [],
      currentFolderId: null,
      rootDriveId: "",
      searchQuery: "",
      viewMode: "list",
      previewFile: null,
      error: null,
      noDriveConfigured: false,
      showUploadDialog: false,
      moveFile: null,
      shareFile: null,
      showGroupManager: false,
      deadlineFile: null,
      editTextFile: null,
      isDriveAdmin: false,
      canWrite: false,
      refreshTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      userToken: "userToken",
      userRole: "userRole",
      // #1470 — canonical (top-level users.role_id), used only on the
      // cs3-context-role header so SAs identify as SA regardless of the
      // per-tenant role they're assuming.
      userCanonicalRole: "userCanonicalRole",
      userClientID: "userClientID",
      userID: "userID",
      employeeId: "employeeId",
    }),
    apiHeaders() {
      return {
        Authorization: this.userToken,
        "cs3-context-role": this.userCanonicalRole,
        "cs3-context-clientId": this.userClientID,
        "cs3-context-employeeId": this.employeeId,
      };
    },
    isAdmin() {
      if (this.isDriveAdmin) return true;
      var slug = this.$store.getters.userRoleSlug || "";
      return ["client-admin", "client_admin", "employer-admin", "employer_admin", "power-user", "power_user"].indexOf(slug) !== -1;
    },
    canUpload() {
      return this.isAdmin || this.canWrite;
    },
  },
  created() {
    this.fetchFiles();
    var self = this;
    this.refreshTimer = setInterval(function () {
      self.fetchFiles();
    }, 30000);
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  },
  methods: {
    fetchFiles() {
      this.loading = true;
      this.afterApi = false;
      this.error = null;
      this.noDriveConfigured = false;
      this.$Progress.start();

      var url = "drive/files";
      var params = [];
      if (this.currentFolderId) {
        params.push("folder_id=" + encodeURIComponent(this.currentFolderId));
      }
      if (this.searchQuery) {
        params.push("search=" + encodeURIComponent(this.searchQuery));
      }
      if (params.length) {
        url += "?" + params.join("&");
      }

      var self = this;
      service
        .get(url, this.apiHeaders)
        .then(function (res) {
          self.$Progress.finish();
          self.loading = false;
          self.afterApi = true;

          if (res.data.status_code === "1") {
            self.files = res.data.result.files || [];
            self.breadcrumbs = res.data.result.breadcrumbs || [];
            self.rootDriveId = res.data.result.folder_id || "";
            if (res.data.result.is_admin !== undefined) {
              self.isDriveAdmin = res.data.result.is_admin;
            }
            self.canWrite = res.data.result.can_write || false;
            if (!self.currentFolderId) {
              self.rootDriveId = res.data.result.folder_id || "";
            }
          } else {
            self.files = [];
            if (res.data.status_code === "0" && res.data.status_message.indexOf("No Drive") !== -1) {
              self.noDriveConfigured = true;
            } else {
              self.error = res.data.status_message;
            }
          }
        })
        .catch(function (err) {
          self.$Progress.fail();
          self.loading = false;
          self.afterApi = true;

          if (err.response && err.response.status === 404) {
            self.noDriveConfigured = true;
          } else {
            self.error = "Failed to load files. Please try again.";
          }
        });
    },

    navigateToFolder(folder) {
      var folderId = typeof folder === "object" ? folder.id : folder;
      this.currentFolderId = folderId;
      this.searchQuery = "";
      this.fetchFiles();
    },

    onSearch(query) {
      this.searchQuery = query;
      this.fetchFiles();
    },

    toggleViewMode() {
      this.viewMode = this.viewMode === "list" ? "grid" : "list";
    },

    /**
     * #1805 — open a payroll workbook in the Wijmo editor, not the preview panel.
     *
     * Routes to the SAME entry point the payroll tab uses — `#/payrollWorkbook/`
     * with the base64 job id — so there is ONE loader path. A second route into
     * the editor would be a second place for the loader contract to drift.
     *
     * THE GATE IS NOT CHECKED HERE, deliberately. `denyUnlessMayOpenPayroll` on
     * the workbook endpoint is the only authority (#1797); a check in this
     * component would be a second one, and the two would eventually disagree. A
     * user with Drive visibility but no seat therefore ROUTES, and the editor
     * surfaces the gate's 403 — a visible refusal rather than a silent failure.
     *
     * No job id means the file declares itself payroll but carries no job — a
     * legacy artefact predating the property with no registry row. Say so rather
     * than opening an empty editor or silently falling back to preview, either of
     * which reads as "the editor is broken".
     */
    openPayrollWorkbook: function (file) {
      var jobId = file && file.payrollJobId;
      if (!jobId) {
        this.$notify
          ? this.$notify.error({
              title: "Cannot open",
              message:
                "This payroll workbook does not record which job it belongs to, so the editor cannot open it. Open it from the job's Payroll tab instead.",
            })
          : window.alert(
              "This payroll workbook does not record which job it belongs to."
            );
        return;
      }
      this.$router.push("/payrollWorkbook/" + btoa(jobId));
    },

    openPreview(file) {
      this.previewFile = file;
    },

    closePreview() {
      this.previewFile = null;
    },

    downloadFile(file) {
      if (file.downloadRestricted && !this.isAdmin) {
        this.$message({ type: "warning", message: "Download is restricted for this file" });
        return;
      }

      this.$message({
        type: "info",
        message: "Downloading " + file.name + "...",
      });

      var self = this;
      var baseUrl = getApiBase();
      var url = baseUrl + "drive/files/" + encodeURIComponent(file.id) + "/download";
      var headers = this.apiHeaders;

      var xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.responseType = "blob";

      safeSetXhrHeaders(xhr, headers);

      var fileName = file.name;
      xhr.onload = function () {
        if (xhr.status === 200) {
          var blob = xhr.response;
          var link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
        } else {
          self.$message({ type: "error", message: "Failed to download file" });
        }
      };

      xhr.onerror = function () {
        self.$message({ type: "error", message: "Failed to download file" });
      };

      xhr.send();
    },

    // --- File management ---

    promptNewFolder() {
      var self = this;
      this.$prompt("Enter folder name", "New Folder", {
        confirmButtonText: "Create",
        cancelButtonText: "Cancel",
        inputPattern: /^.{1,255}$/,
        inputErrorMessage: "Folder name is required (max 255 characters)",
      }).then(function (result) {
        self.createFolder(result.value);
      }).catch(function () {
        // cancelled
      });
    },

    createFolder(name) {
      var self = this;
      this.$Progress.start();

      var body = { name: name };
      if (this.currentFolderId) {
        body.parent_id = this.currentFolderId;
      }

      service
        .post("drive/folders", body, this.apiHeaders)
        .then(function (res) {
          self.$Progress.finish();
          if (res.data.status_code === "1") {
            self.$message({ type: "success", message: 'Folder "' + name + '" created' });
            self.fetchFiles();
          } else {
            self.$message({ type: "error", message: res.data.status_message || "Failed to create folder" });
          }
        })
        .catch(function () {
          self.$Progress.fail();
          self.$message({ type: "error", message: "Failed to create folder" });
        });
    },

    handleUploadFile(payload) {
      var self = this;
      var baseUrl = getApiBase();
      var url = baseUrl + "drive/files/upload";

      var xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);

      var headers = this.apiHeaders;
      safeSetXhrHeaders(xhr, headers);

      xhr.onload = function () {
        if (xhr.status === 200) {
          var resp = JSON.parse(xhr.responseText);
          if (resp.status_code === "1") {
            payload.onSuccess();
          } else {
            self.$message({ type: "error", message: resp.status_message || "Upload failed" });
            payload.onError();
          }
        } else {
          self.$message({ type: "error", message: "Upload failed" });
          payload.onError();
        }
      };

      xhr.onerror = function () {
        self.$message({ type: "error", message: "Upload failed" });
        payload.onError();
      };

      xhr.send(payload.formData);
    },

    onUploadComplete() {
      this.$message({ type: "success", message: "Upload complete" });
      this.showUploadDialog = false;
      var self = this;
      setTimeout(function () {
        self.fetchFiles();
      }, 1500);
    },

    openShareDialog(file) {
      this.shareFile = file;
    },

    promptRename(file) {
      var self = this;
      this.$prompt("Enter new name", "Rename", {
        confirmButtonText: "Rename",
        cancelButtonText: "Cancel",
        inputValue: file.name,
        inputPattern: /^.{1,255}$/,
        inputErrorMessage: "Name is required (max 255 characters)",
      }).then(function (result) {
        if (result.value && result.value !== file.name) {
          self.renameFile(file.id, result.value);
        }
      }).catch(function () {
        // cancelled
      });
    },

    renameFile(fileId, newName) {
      var self = this;
      this.$Progress.start();

      service
        .put("drive/files/" + encodeURIComponent(fileId) + "/rename", { name: newName }, this.apiHeaders)
        .then(function (res) {
          self.$Progress.finish();
          if (res.data.status_code === "1") {
            self.$message({ type: "success", message: "Renamed successfully" });
            self.fetchFiles();
          } else {
            self.$message({ type: "error", message: res.data.status_message || "Rename failed" });
          }
        })
        .catch(function () {
          self.$Progress.fail();
          self.$message({ type: "error", message: "Rename failed" });
        });
    },

    promptMove(file) {
      this.moveFile = file;
    },

    handleLoadFolders(payload) {
      var url = "drive/folders";
      if (payload.parentId) {
        url += "?parent_id=" + encodeURIComponent(payload.parentId);
      }

      service
        .get(url, this.apiHeaders)
        .then(function (res) {
          if (res.data.status_code === "1") {
            payload.callback(res.data.result.folders || []);
          } else {
            payload.callback([]);
          }
        })
        .catch(function () {
          payload.callback([]);
        });
    },

    handleMove(payload) {
      var self = this;
      this.$Progress.start();

      service
        .put("drive/files/" + encodeURIComponent(payload.fileId) + "/move", { parent_id: payload.parentId }, this.apiHeaders)
        .then(function (res) {
          self.$Progress.finish();
          self.moveFile = null;
          if (res.data.status_code === "1") {
            self.$message({ type: "success", message: "Moved successfully" });
            self.fetchFiles();
          } else {
            self.$message({ type: "error", message: res.data.status_message || "Move failed" });
          }
        })
        .catch(function () {
          self.$Progress.fail();
          self.moveFile = null;
          self.$message({ type: "error", message: "Move failed" });
        });
    },

    promptDelete(file) {
      var self = this;
      var label = file.isFolder ? "folder" : "file";
      this.$confirm(
        'Are you sure you want to delete the ' + label + ' "' + file.name + '"?' + (file.isFolder ? " All contents will be deleted." : ""),
        "Delete " + label,
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      ).then(function () {
        self.deleteFile(file.id);
      }).catch(function () {
        // cancelled
      });
    },

    deleteFile(fileId) {
      var self = this;
      this.$Progress.start();

      service
        .del("drive/files/" + encodeURIComponent(fileId), this.apiHeaders)
        .then(function (res) {
          self.$Progress.finish();
          if (res.data.status_code === "1") {
            self.$message({ type: "success", message: "Deleted successfully" });
            self.fetchFiles();
          } else {
            self.$message({ type: "error", message: res.data.status_message || "Delete failed" });
          }
        })
        .catch(function () {
          self.$Progress.fail();
          self.$message({ type: "error", message: "Delete failed" });
        });
    },

    openDeadlineDialog(file) {
      this.deadlineFile = file;
    },

    onDeadlineChanged() {
      this.deadlineFile = null;
      this.fetchFiles();
    },

    openTextEditor(file) {
      this.editTextFile = file;
    },

    handleSaveTextFile(payload) {
      var self = this;
      this.$Progress.start();

      var baseUrl = getApiBase();
      var url = baseUrl + "drive/files/upload";

      // Send content as a file attachment; update ID via header (WAF strips form fields)
      var blob = new Blob([payload.content], { type: "text/html" });
      var formData = new FormData();
      formData.append("file", blob, "update.html");

      var xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      var headers = this.apiHeaders;
      safeSetXhrHeaders(xhr, headers);
      xhr.setRequestHeader("X-CS-Update-FileId", payload.fileId);

      xhr.onload = function () {
        self.$Progress.finish();
        if (xhr.status === 200) {
          try {
            var resp = JSON.parse(xhr.responseText);
            if (resp.status_code === "1") {
              self.$message({ type: "success", message: "File saved" });
              self.editTextFile = null;
              self.fetchFiles();
              return;
            }
          } catch (e) { /* fall through */ }
          self.$message({ type: "error", message: "Save failed" });
        } else {
          self.$message({ type: "error", message: "Failed to save (HTTP " + xhr.status + ")" });
        }
      };
      xhr.onerror = function () {
        self.$Progress.fail();
        self.$message({ type: "error", message: "Failed to save file" });
      };
      xhr.send(formData);
    },

    toggleRestriction(file) {
      var self = this;
      var newState = !file.downloadRestricted;
      var confirmMsg = newState
        ? 'Restrict downloads for "' + file.name + '"? Non-admin users will not be able to download this ' + (file.isFolder ? "folder's contents" : "file") + '.'
        : 'Remove download restriction for "' + file.name + '"?';

      this.$confirm(confirmMsg, "Download Restriction", {
        confirmButtonText: newState ? "Restrict" : "Unrestrict",
        cancelButtonText: "Cancel",
        type: "warning",
      }).then(function () {
        service
          .put(
            "drive/restrictions/" + encodeURIComponent(file.id),
            {
              restricted: newState,
              restrict_visibility: file.visibilityRestricted || false,
              inherit: file.isFolder ? (newState || file.visibilityRestricted) : false,
            },
            self.apiHeaders
          )
          .then(function (res) {
            if (res.data.status_code === "1") {
              self.$message({
                type: "success",
                message: newState ? "Download restricted" : "Download restriction removed",
              });
              self.fetchFiles();
            } else {
              self.$message({ type: "error", message: res.data.status_message || "Failed" });
            }
          })
          .catch(function () {
            self.$message({ type: "error", message: "Failed to update restriction" });
          });
      }).catch(function () {
        // cancelled
      });
    },

    toggleVisibility(file) {
      var self = this;
      var newState = !file.visibilityRestricted;
      var confirmMsg = newState
        ? 'Hide "' + file.name + '" from non-admin users?'
        : 'Show "' + file.name + '" to all users?';

      this.$confirm(confirmMsg, "Visibility", {
        confirmButtonText: newState ? "Hide" : "Show",
        cancelButtonText: "Cancel",
        type: "warning",
      }).then(function () {
        service
          .put(
            "drive/restrictions/" + encodeURIComponent(file.id),
            {
              restricted: file.downloadRestricted || false,
              restrict_visibility: newState,
              inherit: file.isFolder ? (file.downloadRestricted || newState) : false,
            },
            self.apiHeaders
          )
          .then(function (res) {
            if (res.data.status_code === "1") {
              self.$message({
                type: "success",
                message: newState ? "Hidden from non-admins" : "Now visible to all users",
              });
              self.fetchFiles();
            } else {
              self.$message({ type: "error", message: res.data.status_message || "Failed" });
            }
          })
          .catch(function () {
            self.$message({ type: "error", message: "Failed to update visibility" });
          });
      }).catch(function () {
        // cancelled
      });
    },
  },
};
</script>

<style scoped>
.driveVue {
  padding-bottom: 30px;
}
</style>
